import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Typography,
  Switch,
  Slider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import { strategiesAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Strategies = () => {
  const [loading, setLoading] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchStrategies();
  }, [pagination.current, pagination.pageSize]);

  const fetchStrategies = async () => {
    setLoading(true);
    try {
      const response = await strategiesAPI.getAll({
        page: pagination.current,
        limit: pagination.pageSize
      });
      if (response.data.success) {
        setStrategies(response.data.data.strategies);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total
        }));
      }
    } catch (error) {
      message.error('获取策略列表失败');
      console.error('Fetch strategies error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingStrategy(null);
    form.resetFields();
    form.setFieldsValue({ status: 'new', isVip: false, riskLevel: 5 });
    setModalVisible(true);
  };

  const handleEdit = (strategy) => {
    setEditingStrategy(strategy);
    form.setFieldsValue(strategy);
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();
      
      if (editingStrategy) {
        await strategiesAPI.update(editingStrategy.id, values);
        message.success('策略更新成功');
      } else {
        await strategiesAPI.create(values);
        message.success('策略创建成功');
      }
      
      setModalVisible(false);
      fetchStrategies();
    } catch (error) {
      message.error('操作失败');
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await strategiesAPI.delete(id);
      message.success('策略已删除');
      fetchStrategies();
    } catch (error) {
      message.error('删除失败');
      console.error('Delete error:', error);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      render: (title) => (
        <Space>
          <BulbOutlined style={{ color: '#faad14' }} />
          {title}
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const colors = { daily: 'blue', weekly: 'green', signal: 'orange', analysis: 'purple' };
        const labels = { daily: '每日', weekly: '每周', signal: '信号', analysis: '分析' };
        return <Tag color={colors[type]}>{labels[type] || type}</Tag>;
      },
    },
    {
      title: '关注领域',
      dataIndex: 'focusAreas',
      key: 'focusAreas',
      render: (areas) => {
        const parsed = typeof areas === 'string' ? JSON.parse(areas || '[]') : (areas || []);
        return parsed.length > 0 ? parsed.map((area, i) => <Tag key={i}>{area}</Tag>) : '-';
      },
    },
    {
      title: '风险等级',
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      render: (level) => {
        const color = level <= 3 ? 'green' : level <= 6 ? 'orange' : 'red';
        return <Tag color={color}>{level || 0}/10</Tag>;
      },
    },
    {
      title: 'VIP',
      dataIndex: 'isVip',
      key: 'isVip',
      render: (isVip) => isVip ? <Tag color="gold">VIP</Tag> : <Tag>免费</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = { new: 'blue', active: 'green', completed: 'default', cancelled: 'red' };
        const labels = { new: '新发布', active: '进行中', completed: '已完成', cancelled: '已取消' };
        return <Tag color={colors[status]}>{labels[status] || status}</Tag>;
      },
    },
    {
      title: '发布时间',
      dataIndex: 'publishedAt',
      key: 'publishedAt',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>首页交易员策略管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          发布策略
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={strategies}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          locale={{ emptyText: '暂无策略数据' }}
        />
      </Card>

      <Modal
        title={editingStrategy ? '编辑策略' : '发布策略'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={700}
        okText="保存"
        cancelText="取消"
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label="类型" rules={[{ required: true, message: '请选择类型' }]}>
            <Select>
              <Option value="daily">每日策略</Option>
              <Option value="weekly">每周策略</Option>
              <Option value="signal">交易信号</Option>
              <Option value="analysis">深度分析</Option>
            </Select>
          </Form.Item>
          <Form.Item name="marketAnalysis" label="市场分析" rules={[{ required: true, message: '请输入市场分析' }]}>
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item name="focusAreas" label="关注领域">
            <Select mode="tags" placeholder="输入关注领域">
              <Option value="Technology">科技</Option>
              <Option value="Healthcare">医疗</Option>
              <Option value="Financials">金融</Option>
              <Option value="Energy">能源</Option>
              <Option value="AI Stocks">AI股票</Option>
            </Select>
          </Form.Item>
          <Form.Item name="riskLevel" label="风险等级 (1-10)">
            <Slider min={1} max={10} marks={{ 1: '低', 5: '中', 10: '高' }} />
          </Form.Item>
          <Form.Item name="riskWarning" label="风险提示">
            <TextArea rows={2} />
          </Form.Item>
          <Space>
            <Form.Item name="isVip" label="VIP专属" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select style={{ width: 120 }}>
                <Option value="new">新发布</Option>
                <Option value="active">进行中</Option>
                <Option value="completed">已完成</Option>
                <Option value="cancelled">已取消</Option>
              </Select>
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </Space>
  );
};

export default Strategies;
